import { isNil } from 'lodash';
import getSymbol from 'currency-symbol-map';

// Proper nouns that should always be capitalized (single words only, matched per-word)
const PROPER_NOUNS = new Set([
  'accenture',
  'adobe',
  'airbnb',
  'alibaba',
  'alphabet',
  'amazon',
  'amd',
  'apple',
  'baidu',
  'berkshire',
  'broadcom',
  'chevron',
  'cisco',
  'cloudflare',
  'coca-cola',
  'costco',
  'delta',
  'disney',
  'exxon',
  'fed',
  'ford',
  'gemini',
  'goldman',
  'google',
  'hathaway',
  'instagram',
  'intel',
  'meta',
  'micron',
  'microsoft',
  'mobil',
  'netflix',
  'nvidia',
  'oracle',
  'palantir',
  'powell',
  'qualcomm',
  'sachs',
  'salesforce',
  'siri',
  'spotify',
  'tesla',
  'uber',
  'walmart',
]);

/**
 * Convert text to sentence case while preserving proper nouns and acronyms.
 * - Preserves acronyms (CEO, NASDAQ, AI)
 * - Preserves mixed case words (iPhone, CoreWeave)
 * - Preserves proper nouns (Google, Meta, Nvidia, etc.)
 * - Handles punctuation properly
 */
export const toSentenceCase = (text: string): string => {
  if (!text) return text;

  const words = text.split(/\s+/);

  return words
    .map((word, index) => {
      // Extract trailing punctuation only
      const trailingPunctuationMatch = word.match(/[.,;:!?'"()]+$/);
      const trailingPunctuation = trailingPunctuationMatch ? trailingPunctuationMatch[0] : '';
      const wordWithoutTrailingPunctuation = word.slice(0, word.length - trailingPunctuation.length);

      // Remove all punctuation for checking capitalization patterns
      const wordWithoutPunctuation = wordWithoutTrailingPunctuation.replace(/[.,;:!?'"()]/g, '');

      // Check if word has mid-word capitals (like iPhone, STMicroelectronics, CoreWeave)
      const hasMidWordCaps =
        wordWithoutPunctuation.length > 1 &&
        /[a-z]/.test(wordWithoutPunctuation) &&
        /[A-Z]/.test(wordWithoutPunctuation.slice(1));

      // Check if it's an all-caps acronym or abbreviation (2+ letters)
      const isAllCaps =
        wordWithoutPunctuation.length >= 2 &&
        wordWithoutPunctuation === wordWithoutPunctuation.toUpperCase() &&
        /[A-Z]/.test(wordWithoutPunctuation);

      // Preserve original capitalization for acronyms and mixed case words
      if (hasMidWordCaps || isAllCaps) {
        return word;
      }

      // Check if it's a proper noun (case-insensitive match)
      if (PROPER_NOUNS.has(wordWithoutPunctuation.toLowerCase())) {
        return (
          wordWithoutTrailingPunctuation.charAt(0).toUpperCase() +
          wordWithoutTrailingPunctuation.slice(1).toLowerCase() +
          trailingPunctuation
        );
      }

      // First word: capitalize first letter, lowercase the rest
      if (index === 0) {
        return (
          wordWithoutTrailingPunctuation.charAt(0).toUpperCase() +
          wordWithoutTrailingPunctuation.slice(1).toLowerCase() +
          trailingPunctuation
        );
      }

      // All other words: lowercase
      return word.toLowerCase();
    })
    .join(' ');
};

export enum FormatterStyle {
  moneyBig,
  moneyBigDetailed,
  moneySmall,
  moneyCompact, // $1.2K, $3.4M, $5.6B for compact display
  number,
  percent,
  percentDetailed,
  percentDetailed2,
  percentChange,
  percentChangeCompact, // +1.2K% for large percentages (value already in %)
}

export default (type: FormatterStyle, value?: number, currency?: string) => {
  if (isNil(value)) {
    return '';
  }

  const currencySymbol = currency ? getSymbol(currency) || '$' : '$';

  const formatters = {
    [FormatterStyle.moneyBig]: () => {
      const absValue = Math.abs(value);

      if (absValue >= 1_000_000_000_000) {
        return `${currencySymbol}${(value / 1_000_000_000_000).toFixed(1)}\u200AT`;
      }
      if (absValue >= 1_000_000_000) {
        return `${currencySymbol}${(value / 1_000_000_000).toFixed(1)}\u200AB`;
      }
      return `${currencySymbol}${(value / 1_000_000).toFixed(1)}\u200AM`;
    },

    [FormatterStyle.moneyBigDetailed]: () => {
      const absValue = Math.abs(value);

      if (absValue >= 1_000_000_000_000) {
        return `${currencySymbol}${(value / 1_000_000_000_000).toFixed(1)} Trillion`;
      }
      if (absValue >= 1_000_000_000) {
        return `${currencySymbol}${(value / 1_000_000_000).toFixed(1)} Billion`;
      }
      return `${currencySymbol}${(value / 1_000_000).toFixed(1)} Million`;
    },

    [FormatterStyle.moneySmall]: () =>
      currencySymbol + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),

    [FormatterStyle.moneyCompact]: () => {
      const absValue = Math.abs(value);
      let displayNumber: string;
      let unit: string;

      if (absValue >= 10 ** 12) {
        displayNumber = (value / 10 ** 12).toFixed(1);
        unit = 'T';
      } else if (absValue >= 10 ** 9) {
        displayNumber = (value / 10 ** 9).toFixed(1);
        unit = 'B';
      } else if (absValue >= 10 ** 6) {
        displayNumber = (value / 10 ** 6).toFixed(1);
        unit = 'M';
      } else if (absValue >= 10 ** 3) {
        displayNumber = (value / 10 ** 3).toFixed(1);
        unit = 'K';
      } else {
        return currencySymbol + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      // Remove trailing .0
      if (displayNumber.endsWith('.0')) {
        displayNumber = displayNumber.slice(0, -2);
      }

      return `${currencySymbol}${displayNumber}${unit}`;
    },

    [FormatterStyle.number]: () => value.toFixed(2),

    [FormatterStyle.percent]: () => `${Math.round(value * 100)}%`,

    [FormatterStyle.percentDetailed]: () => `${(value * 100).toFixed(1)}%`,

    [FormatterStyle.percentDetailed2]: () => `${(value * 100).toFixed(2)}%`,

    [FormatterStyle.percentChange]: () => `${value > 0 ? '+' : '-'} ${Math.abs(value * 100).toFixed(0)}%`,

    [FormatterStyle.percentChangeCompact]: () => {
      // Value is already in percentage form (e.g., 426.9 means 426.9%)
      const sign = value >= 0 ? '+' : '';
      const absValue = Math.abs(value);

      if (absValue >= 1000) {
        const displayNumber = (value / 1000).toFixed(1);
        // Remove trailing .0
        const cleanNumber = displayNumber.endsWith('.0') ? displayNumber.slice(0, -2) : displayNumber;
        return `${sign}${cleanNumber}K%`;
      }

      return `${sign}${value.toFixed(1)}%`;
    },
  } as any;

  return formatters[type]();
};
